import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import authService from '../services/api/auth.service';
import axios from 'axios';

interface UserData {
    id: string;
    profile_img: string | null;
    first_name: string;
    last_name: string;
    phone_no: string;
    email: string;
    role_id: number;
    status: string;
    is_email_verified: boolean | null;
    is_phone_verified: boolean;
    created_at: string;
    updated_at: string;
    customer: {
        id: string;
        user_id: string;
        partner_id: string | null;
        address_line_1: string | null;
        address_line_2: string | null;
        city: string | null;
        state: string | null;
        country: string | null;
        pincode: string | null;
        dob: string | null;
        pan: string;
        cas_reference_id: string | null;
        sso_token: string | null;
        external_id: string | null;
        customer_code: string | null;
        gender: string | null;
        application_status: string | null;
        createdAt: string;
        updatedAt: string;
    };
}

interface PartnerDetails {
    partner_id: string;
    company_logo: string;
    company_domain: string;
    company_name: string;
    company_email: string;
    company_wa_no: string;
    company_contact_no: string;
    status: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    token: string | null;
    login: (token: string) => Promise<void>;
    logout: () => Promise<void>;
    loading: boolean;
    userData: UserData | null;
    partnerData: PartnerDetails | null;
    userDataLoading: boolean;
    fetchUserData: () => Promise<void>;
}

interface AuthProviderProps {
    children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [userDataLoading, setUserDataLoading] = useState<boolean>(true);
    const [partnerData, setPartnerData] = useState<PartnerDetails | null>(null);
    const [whitelabelLoading, setWhitelabelLoading] = useState<boolean>(true);
    const navigate = useNavigate();

    useEffect(() => {
        getPartnerDetails();
        const storedToken = Cookies.get('lark-customerauth-token');
        if (storedToken) {
            setToken(storedToken);
            setIsAuthenticated(true);
        }
        setLoading(false);
    }, []);

    const fetchUserData = async () => {
        setUserDataLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/customer/auth/profile`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Signature: process.env.REACT_APP_SIGNATURE
                }
            });
            setUserData(response.data.data);
        } catch (error) {
            // await logout();
        } finally {
            setUserDataLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchUserData();
        }
    }, [token]);

    const login = async (token: string) => {
        try {
            Cookies.set('lark-customerauth-token', token);
            setToken(token);
            setIsAuthenticated(true);
            await fetchUserData();
            navigate('/');
        } catch (error) {
            console.error('Login failed:', error);
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setToken(null);
            setIsAuthenticated(false);
            setUserData(null);
            Cookies.remove('lark-customerauth-token');
            navigate('/login');
        }
    };

    const getDomainFromUrl = (url: string) => {
        const urlParts = url.split('.');
        const domain = urlParts[0];
        return domain;
    };

    const getPartnerDetails = async () => {
        try {
            const domain = getDomainFromUrl(window.location.hostname);
            const response = await authService.getPartner(domain);
            setPartnerData(response.data);
            return response;
        } catch (error) {
            // do nothing
        } finally {
            setWhitelabelLoading(false);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                token,
                login,
                partnerData,
                logout,
                loading,
                userData,
                userDataLoading,
                fetchUserData
            }}
        >
            {whitelabelLoading ? <LoadingOverlay /> : children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const useLoginNavigation = () => {
    const { login } = useAuth();
    const [isLoginComplete, setIsLoginComplete] = useState<boolean>(false);

    const handleLogin = async (token: string) => {
        try {
            await login(token);

            setIsLoginComplete(true);
        } catch (error) {
            setIsLoginComplete(false);
        }
    };

    return { handleLogin, isLoginComplete };
};


export const LoadingOverlay = () => {

    return (
        <div
            className={`fixed top-0 left-0 w-full h-full bg-white bg-opacity-50 z-50 flex justify-center items-center `}
        >
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        </div>
    );
};
